import { useEffect } from 'react';

const DEFAULT_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/17b3d771-b1f5-4098-a241-9e4a23cb7105/images/c8b7eb93de2da3a02ceb808064cf24055b335885c45e7ef8d0168feda2a8a754.png';
const SITE_NAME = 'The Watchtower';
const SITE_URL = 'https://the-watchtower.app';
const DEFAULT_TITLE = 'The Watchtower - UK Political News & Accountability';
const DEFAULT_DESCRIPTION = 'Track UK political scandals, MP misconduct, and council corruption. Independent news aggregation covering 649 MPs and 19,000+ councillors. Get alerts on political accountability.';

// SEO Keywords for targeting
const DEFAULT_KEYWORDS = 'UK politics, political scandals UK, MP news, councillor news, UK political accountability, Westminster scandals, local council news, track politicians UK, political corruption UK, Labour news, Conservative news, Reform UK news';

export default function SEO({ 
  title, 
  description, 
  image,
  url,
  type = 'website',
  article = null,
  politician = null,
  keywords = null
}) {
  const pageTitle = title ? `${String(title)} | ${SITE_NAME}` : DEFAULT_TITLE;
  const pageDescription = description ? String(description).substring(0, 160) : DEFAULT_DESCRIPTION;
  const pageImage = image || DEFAULT_IMAGE;
  const pageUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const pageKeywords = keywords || DEFAULT_KEYWORDS;

  useEffect(() => {
    // Update document title
    document.title = pageTitle;
    
    // Update or create meta tags
    const updateMetaTag = (selector, content, property = false) => {
      let meta = document.querySelector(selector);
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', selector.replace('meta[property="', '').replace('"]', ''));
        } else {
          meta.setAttribute('name', selector.replace('meta[name="', '').replace('"]', ''));
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Primary meta tags
    updateMetaTag('meta[name="description"]', pageDescription);
    updateMetaTag('meta[name="keywords"]', pageKeywords);
    updateMetaTag('meta[name="author"]', 'The Watchtower');
    updateMetaTag('meta[name="robots"]', 'index, follow');
    
    // Open Graph
    updateMetaTag('meta[property="og:type"]', type, true);
    updateMetaTag('meta[property="og:url"]', pageUrl, true);
    updateMetaTag('meta[property="og:title"]', pageTitle, true);
    updateMetaTag('meta[property="og:description"]', pageDescription, true);
    updateMetaTag('meta[property="og:image"]', pageImage, true);
    updateMetaTag('meta[property="og:site_name"]', SITE_NAME, true);
    updateMetaTag('meta[property="og:locale"]', 'en_GB', true);
    
    // Twitter
    updateMetaTag('meta[name="twitter:card"]', 'summary_large_image');
    updateMetaTag('meta[name="twitter:title"]', pageTitle);
    updateMetaTag('meta[name="twitter:description"]', pageDescription);
    updateMetaTag('meta[name="twitter:image"]', pageImage);
    updateMetaTag('meta[name="twitter:url"]', pageUrl);
    
    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', pageUrl);

    // JSON-LD Structured Data
    const existingScript = document.querySelector('script[data-seo="structured-data"]');
    if (existingScript) {
      existingScript.remove();
    }

    const structuredData = generateStructuredData(type, {
      title: pageTitle,
      description: pageDescription,
      url: pageUrl,
      image: pageImage,
      article,
      politician
    });

    if (structuredData) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo', 'structured-data');
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }

    // Cleanup on unmount
    return () => {
      document.title = DEFAULT_TITLE;
      const structuredScript = document.querySelector('script[data-seo="structured-data"]');
      if (structuredScript) structuredScript.remove();
    };
  }, [pageTitle, pageDescription, pageImage, pageUrl, type, pageKeywords, article, politician]);

  return null;
}

// Generate JSON-LD structured data based on page type
function generateStructuredData(type, data) {
  const baseOrg = {
    "@type": "Organization",
    "name": "The Watchtower",
    "url": "https://the-watchtower.app",
    "logo": DEFAULT_IMAGE,
    "description": "Independent UK political accountability platform",
    "sameAs": []
  };

  switch (type) {
    case 'website':
      return {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": SITE_NAME,
        "url": SITE_URL,
        "description": DEFAULT_DESCRIPTION,
        "publisher": baseOrg,
        "potentialAction": {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": `${SITE_URL}/politicians?q={search_term_string}`
          },
          "query-input": "required name=search_term_string"
        }
      };

    case 'article':
      if (!data.article) return null;
      return {
        "@context": "https://schema.org",
        "@type": "NewsArticle",
        "headline": data.article.title,
        "description": data.description,
        "image": data.image,
        "url": data.url,
        "datePublished": data.article.published_at,
        "dateModified": data.article.published_at,
        "author": {
          "@type": "Organization",
          "name": data.article.source || "The Watchtower"
        },
        "publisher": baseOrg,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": data.url
        },
        "articleSection": "UK Politics",
        "keywords": data.article.party ? `${data.article.party}, UK politics, political news` : "UK politics"
      };

    case 'profile':
      if (!data.politician) return null;
      return {
        "@context": "https://schema.org",
        "@type": "Person",
        "name": data.politician.name,
        "jobTitle": data.politician.politician_type || "Politician",
        "description": data.description,
        "url": data.url,
        "image": data.politician.photo_url || data.image,
        "affiliation": {
          "@type": "PoliticalParty",
          "name": data.politician.party
        },
        "worksFor": data.politician.politician_type === 'MP' ? {
          "@type": "GovernmentOrganization",
          "name": "UK Parliament"
        } : {
          "@type": "GovernmentOrganization", 
          "name": data.politician.council || "Local Council"
        }
      };

    default:
      return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": data.title,
        "description": data.description,
        "url": data.url,
        "publisher": baseOrg
      };
  }
}

// Export helper for generating page-specific SEO data
export const generatePageSEO = {
  home: () => ({
    title: null,
    description: 'Track UK political scandals and hold MPs accountable. Independent news aggregation covering Westminster and local councils. 649 MPs, 19,000+ councillors monitored 24/7.',
    keywords: 'UK political news, MP scandals, councillor corruption, Westminster news, political accountability UK, track politicians'
  }),
  
  news: () => ({
    title: 'Latest UK Political News',
    description: 'Breaking political news from across the UK. Scandals, controversies and accountability stories from Parliament and local councils.',
    keywords: 'UK political news today, breaking political news UK, Westminster scandals, Labour news, Conservative news, political controversies'
  }),
  
  politicians: () => ({
    title: 'Search UK Politicians - MPs & Councillors',
    description: 'Search and track any UK politician. Database of 649 MPs and 19,000+ councillors. Find your local representative and see their news coverage.',
    keywords: 'UK MPs list, find my MP, UK councillors, local politicians, search politicians UK, MP database'
  }),
  
  elections: () => ({
    title: 'UK Elections & By-Elections',
    description: 'Track upcoming UK elections and by-elections. Results, candidates and constituency information for Westminster and local councils.',
    keywords: 'UK elections 2026, by-elections UK, election results, voting UK, constituency results, council elections'
  }),
  
  polls: () => ({
    title: 'UK Political Polls & Opinion Tracker',
    description: 'Latest UK political polling data. Track party standings, approval ratings and voting intentions across Britain.',
    keywords: 'UK polls, political polling UK, Labour polls, Conservative polls, voting intentions UK, opinion polls'
  }),
  
  map: () => ({
    title: 'UK Political Map - Find Your MP',
    description: 'Interactive map of UK constituencies and councils. Find your MP by postcode and explore local political representation.',
    keywords: 'UK constituency map, find MP by postcode, political map UK, council map, local councillors map'
  }),
  
  aiResearch: () => ({
    title: 'AI Political Research Assistant',
    description: 'Ask questions about UK politicians and get AI-powered research. Investigate MPs, councillors and political issues with our research tool.',
    keywords: 'political research UK, investigate politicians, MP research, councillor background check, political AI assistant'
  }),

  politician: (name, party, type) => ({
    title: `${name} - ${party} ${type}`,
    description: `News, voting record and contact information for ${name}, ${party} ${type}. Track their political activity and controversies.`,
    keywords: `${name}, ${party} ${type}, ${name} news, ${name} scandals, ${name} voting record`
  }),

  article: (title, party, source) => ({
    title: title,
    description: `${title}. Political news from ${source} covering ${party || 'UK politics'}.`,
    keywords: `${party || 'UK politics'}, political news, ${source}, Westminster, political scandal`
  })
};
